import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { useSession } from "../auth/SessionProvider";

/**
 * Subscribes to one private Broadcast topic while a route is mounted.
 *
 * architecture.md section 8 governs every line here:
 *
 * - private channels only, so `config.private` is always true and never a prop;
 * - the four approved topic shapes and nothing else;
 * - a received event carries an event type and a row ID, so the handler invalidates a query and
 *   lets the ordinary RLS read path fetch the authorised projection. The payload is never rendered;
 * - realtime is an enhancement. A missed event is repaired by the invalidation this hook performs
 *   on reconnect, so no screen waits for an event to be correct.
 */

export type PortalTopic =
  | { kind: "tenant"; id: string; channel: "chat" | "requests" | "incidents" }
  | { kind: "user"; id: string; channel: "notifications" };

export function topicName(topic: PortalTopic): string {
  return `${topic.kind}:${topic.id}:${topic.channel}`;
}

export interface PortalChannelOptions {
  /** Query keys to invalidate when an event arrives, and on every (re)subscribe. */
  invalidate: readonly (readonly unknown[])[];
  enabled?: boolean;
}

export function usePortalChannel(
  topic: PortalTopic | null,
  { invalidate, enabled = true }: PortalChannelOptions
): void {
  const { status, session } = useSession();
  const queryClient = useQueryClient();
  const accessToken = session?.access_token ?? null;
  const name = topic ? topicName(topic) : null;
  const invalidateKey = JSON.stringify(invalidate);

  useEffect(() => {
    if (!enabled || !name || status !== "signed-in" || !accessToken) {
      return;
    }

    const supabase = getSupabaseBrowserClient();
    const keys = JSON.parse(invalidateKey) as unknown[][];
    const refresh = () => {
      for (const key of keys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    };

    // Realtime authorises the join against `realtime.messages` policies using this token, so it has
    // to be the current one. An expired token is refused at join rather than downgraded.
    supabase.realtime.setAuth(accessToken);

    const channel: RealtimeChannel = supabase
      .channel(name, { config: { private: true } })
      .on("broadcast", { event: "*" }, () => {
        // Deliberately ignores the payload. The event says something changed; the query says what.
        refresh();
      })
      .subscribe((state) => {
        if (state === "SUBSCRIBED") {
          // Repairs anything missed while disconnected.
          refresh();
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, name, status, accessToken, invalidateKey, queryClient]);
}
